import React, { useRef, useCallback, useState } from 'react';
import { Form } from 'react-bootstrap';

/**
 * Input con navegación para estabilidad.
 * Formato ES: display con coma decimal `,`, storage con `.`.
 * onWheel bloquea cambio de valor por scroll (bug UX).
 */
const EstabilidadInput = ({ value, onChange, placeholder, inputRef, onNavigate }) => {
  const hasValue = value !== '' && value !== undefined && value !== null;
  // Estado local para preservar separador trailing (ver MedicionInput en Puntosmediciontable.jsx).
  const [localStr, setLocalStr] = useState(null);

  const displayValue = React.useMemo(() => {
    if (localStr !== null) return localStr;
    if (value === '' || value === undefined || value === null) return '';
    return String(value).replace('.', ',');
  }, [value, localStr]);

  const handleChange = (rawInput) => {
    let s = String(rawInput).replace(/[^0-9,.\-]/g, '');
    const firstSep = s.search(/[,.]/);
    if (firstSep !== -1) {
      s = s.slice(0, firstSep + 1) + s.slice(firstSep + 1).replace(/[,.]/g, '');
    }
    const displayForm = s.replace('.', ',');
    setLocalStr(displayForm);
    const storageForm = s.replace(',', '.');
    if (storageForm === '' || storageForm === '-') {
      onChange('');
      return;
    }
    if (storageForm.endsWith('.')) return;
    onChange(storageForm);
  };

  const handleBlur = () => setLocalStr(null);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
    if (onNavigate) {
      if (e.key === 'ArrowUp') {
        onNavigate('up');
      } else if (e.key === 'ArrowDown') {
        onNavigate('down');
      } else if (e.key === 'ArrowLeft') {
        onNavigate('left');
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        onNavigate('right');
      }
    }
  };

  const handleWheel = (e) => { e.currentTarget.blur(); };

  return (
    <Form.Control
      type="text"
      inputMode="decimal"
      autoComplete="off"
      size="sm"
      value={displayValue}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      placeholder={placeholder}
      className={`medicion-input ${hasValue ? 'medicion-input--filled' : ''}`}
      ref={inputRef}
    />
  );
};

/**
 * Componente de tabla para registrar mediciones de estabilidad
 * Una fila por punto, columnas según cantMedicionesEstabilidad
 */
const EstabilidadTable = ({
  magnitud,
  datosEstabilidad,
  datosPuntosCalibracion,
  criteriosPorPunto,
  onChangeMedicion,
  onChangeCriterio
}) => {

  const { cantidadPuntos: cantPuntos, cantMedicionesEstabilidad = 10 } = magnitud;

  // Refs para navegación entre inputs
  const inputRefs = useRef({});

  // Función para obtener/crear ref
  const getInputRef = (puntoIdx, medicionIdx) => {
    const key = `${puntoIdx}-${medicionIdx}`;
    if (!inputRefs.current[key]) {
      inputRefs.current[key] = React.createRef();
    }
    return inputRefs.current[key];
  };

  // Función para navegar entre celdas
  const handleNavigate = useCallback((puntoIdx, medicionIdx, direction) => {
    let newPuntoIdx = puntoIdx;
    let newMedicionIdx = medicionIdx;

    switch (direction) {
      case 'up':
        newPuntoIdx = Math.max(0, puntoIdx - 1);
        break;
      case 'down':
        newPuntoIdx = Math.min(cantPuntos - 1, puntoIdx + 1);
        break;
      case 'left':
        if (medicionIdx > 0) {
          newMedicionIdx = medicionIdx - 1;
        } else if (puntoIdx > 0) {
          newPuntoIdx = puntoIdx - 1;
          newMedicionIdx = cantMedicionesEstabilidad - 1;
        }
        break;
      case 'right':
        if (medicionIdx < cantMedicionesEstabilidad - 1) {
          newMedicionIdx = medicionIdx + 1;
        } else if (puntoIdx < cantPuntos - 1) {
          newPuntoIdx = puntoIdx + 1;
          newMedicionIdx = 0;
        }
        break;
      default:
        return;
    }

    // Enfocar el nuevo input
    const newKey = `${newPuntoIdx}-${newMedicionIdx}`;
    const newRef = inputRefs.current[newKey];
    
    if (newRef && newRef.current) {
      newRef.current.focus();
      newRef.current.select();
    }
  }, [cantPuntos, cantMedicionesEstabilidad]);

  // Determinar color de fondo por punto
  const getPuntoBackgroundColor = (puntoIdx) => {
    return puntoIdx % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
  };

  return (
    <div className="mediciones-table-container">
      <table className="mediciones-table">
        <thead>
          <tr>
            <th className="mediciones-table__th mediciones-table__th--left mediciones-table__th--width-100">
              Punto
            </th>
            <th className="mediciones-table__th mediciones-table__th--width-100" title="Variación máxima permitida en este punto">
              Criterio
            </th>
            {Array.from({ length: cantMedicionesEstabilidad }).map((_, idx) => (
              <th key={idx} className="mediciones-table__th">
                M{idx + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: cantPuntos }).map((_, puntoIdx) => {
            const mediciones = datosEstabilidad?.[puntoIdx] || [];
            const valorPunto = datosPuntosCalibracion?.[puntoIdx] || `Punto ${puntoIdx + 1}`;
            const criterio = criteriosPorPunto?.[puntoIdx] ?? '';
            const bgColor = getPuntoBackgroundColor(puntoIdx);

            return (
              <tr key={puntoIdx}>
                <td
                  className="mediciones-table__td"
                  style={{ backgroundColor: bgColor }}
                >
                  <span className="mediciones-table__punto-label">
                    {valorPunto}
                  </span>
                </td>
                <td
                  className="mediciones-table__td mediciones-table__td--input"
                  style={{ backgroundColor: bgColor }}
                >
                  <EstabilidadInput
                    value={criterio}
                    onChange={(v) => onChangeCriterio?.(puntoIdx, v)}
                    placeholder="0.05"
                  />
                </td>
                {Array.from({ length: cantMedicionesEstabilidad }).map((_, medIdx) => (
                  <td
                    key={medIdx}
                    className="mediciones-table__td mediciones-table__td--input"
                    style={{ backgroundColor: bgColor }}
                  >
                    <EstabilidadInput
                      value={mediciones[medIdx] ?? ''}
                      onChange={(v) => onChangeMedicion(puntoIdx, medIdx, v)}
                      placeholder={`M${medIdx + 1}`}
                      inputRef={getInputRef(puntoIdx, medIdx)}
                      onNavigate={(dir) => handleNavigate(puntoIdx, medIdx, dir)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EstabilidadTable;
